import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProducts } from '../../services/api';
import { Product } from '../../types';
import { PlusCircle, Edit, ImagePlus, ExternalLink, AlertCircle, Filter, Search, SlidersHorizontal, X, LayoutGrid, List, Eye } from 'lucide-react';
import ProductDetailModal from '../../components/products/ProductDetailModal';
import ProductEditModal from '../../components/products/ProductEditModal';
import ProductOnboardingModal from '../../components/products/ProductOnboardingModal';
import ContentLoader from '../../components/ui/ContentLoader';
import { usePermissions } from '../../hooks/usePermissions';

const ProductsListPage: React.FC = () => {
  const { canManageProducts } = usePermissions();
  const [products, setProducts] = useState<Product[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  const [selectedProductForEdit, setSelectedProductForEdit] = useState<string | null>(null);
  const [productTypeFilter, setProductTypeFilter] = useState<'all' | 'saas' | 'logger'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  const loadProducts = async (isInitial = true) => {
    if (isInitial) {
      setIsInitialLoading(true);
    } else {
      setIsRefreshing(true);
    }
    try {
      const data = await getProducts();
      data.sort((a, b) => a.name.localeCompare(b.name));
      setProducts(data);
      setError(null);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setIsInitialLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadProducts(true);
  }, []);

  // Load saved view mode preference - but always default to grid view
  useEffect(() => {
    // Always set to grid view by default
    setViewMode('grid');
    // Still save the preference when user changes it
    localStorage.setItem('products-view-mode', 'grid');
  }, []);

  // Toggle view mode between grid and table
  const toggleViewMode = (mode: 'grid' | 'table') => {
    setViewMode(mode);
    localStorage.setItem('products-view-mode', mode);
  };

  // Apply filters and search
  const filteredProducts = products.filter(product => {
    // Apply product type filter
    if (productTypeFilter !== 'all' && (product as any).type !== productTypeFilter) {
      return false;
    }
    
    // Apply search filter if search query exists
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        product.name.toLowerCase().includes(query) || 
        product.tags?.productName?.toLowerCase().includes(query) ||
        product.tags?.productModule?.toLowerCase().includes(query) ||
        product.tags?.productType?.toLowerCase().includes(query) ||
        product.tags?.logType?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  // Count products by type for filter badges, based on currently visible products
  const productCounts = {
    all: products.length,
    saas: products.filter(p => (p as any).type === 'saas').length,
    logger: products.filter(p => (p as any).type === 'logger').length
  };

  // Open the product detail modal
  const openProductDetailModal = (productId: string) => {
    setSelectedProductId(productId);
    setIsDetailModalOpen(true);
  };
  
  // Close the product detail modal
  const closeProductDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedProductId(null);
  };

  // Handle edit modal
  const openProductEditModal = (productId: string) => {
    setSelectedProductForEdit(productId);
    setIsEditModalOpen(true);
  };
  
  const closeProductEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedProductForEdit(null);
  };
  
  // Handle onboarding modal
  const openOnboardingModal = () => {
    setIsOnboardingModalOpen(true);
  };
  
  const closeOnboardingModal = () => {
    setIsOnboardingModalOpen(false);
  };
  
  // Reload products after save
  const handleProductSaved = async () => {
    await loadProducts(false);
  };

  // Clear all filters
  const clearFilters = () => {
    setProductTypeFilter('all');
    setSearchQuery('');
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const showContentLoading = isInitialLoading || isRefreshing;

  return (
    <div className="p-6">
      {/* Header section with gradient background */}
      <div className="mb-6 rounded-lg overflow-hidden relative z-10">
        <div className="bg-gradient-to-b from-gray-900/90 via-indigo-900/20 to-transparent p-6 rounded-xl backdrop-blur-sm border border-indigo-800/20 shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">Product Management</h1>
              <p className="text-gray-400 mt-1">
                Manage your products and their integration fields
              </p>
            </div>
            {canManageProducts() && (
              <button 
                onClick={openOnboardingModal}
                className="relative z-10 py-2 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-md shadow-lg flex items-center transition-all duration-200 hover:shadow-xl"
              >
                <PlusCircle size={16} className="mr-2" />
                Add Product
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-status-danger/10 border border-status-danger/20 text-status-danger rounded-md p-4 mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* Enhanced Filter and Search Bar */}
      <div className="mb-6 bg-background-card border border-card-border rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 bg-background-hover border border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              placeholder="Search products by name, type, or tags..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setSearchQuery('')}
              >
                <X size={16} className="text-gray-400 hover:text-white transition-colors" />
              </button>
            )}
          </div>

          {/* Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg border transition-all ${
              showFilters 
                ? 'bg-primary/20 border-primary/40 text-primary-300' 
                : 'border-border-dark bg-background-hover text-gray-300 hover:bg-background-darker'
            }`}
          >
            <SlidersHorizontal size={16} className="mr-2" />
            Filters
            {(productTypeFilter !== 'all' || searchQuery) && (
              <span className="ml-2 bg-primary/30 text-primary-300 text-xs py-0.5 px-2 rounded-full">
                {(productTypeFilter !== 'all' ? 1 : 0) + (searchQuery ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* Expanded Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border-dark animate-fadeIn">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center text-gray-400 mr-2">
                <Filter size={16} className="mr-1" />
                <span className="text-sm">Product Type:</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <button
                  className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                    productTypeFilter === 'all' 
                      ? 'bg-primary text-white shadow-md shadow-primary/20' 
                      : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                  }`}
                  onClick={() => setProductTypeFilter('all')}
                >
                  All
                  <span className="ml-2 bg-white/20 text-white text-xs py-0.5 px-2 rounded-full">
                    {productCounts.all}
                  </span>
                </button>
                <button
                  className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                    productTypeFilter === 'saas' 
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' 
                      : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                  }`}
                  onClick={() => setProductTypeFilter('saas')}
                >
                  SaaS
                  <span className={`ml-2 text-xs py-0.5 px-2 rounded-full ${
                    productTypeFilter === 'saas'
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-700/20 text-blue-300'
                  }`}>
                    {productCounts.saas}
                  </span>
                </button>
                <button
                  className={`px-4 py-1.5 text-sm rounded-full transition-all flex items-center ${
                    productTypeFilter === 'logger' 
                      ? 'bg-green-600 text-white shadow-md shadow-green-500/20' 
                      : 'bg-background-hover text-text-muted hover:bg-background-darker border border-border-dark'
                  }`}
                  onClick={() => setProductTypeFilter('logger')}
                >
                  Logger
                  <span className={`ml-2 text-xs py-0.5 px-2 rounded-full ${
                    productTypeFilter === 'logger'
                      ? 'bg-white/20 text-white'
                      : 'bg-green-700/20 text-green-300'
                  }`}>
                    {productCounts.logger}
                  </span>
                </button>
              </div>

              {/* Clear Filters Button */}
              {(productTypeFilter !== 'all' || searchQuery) && (
                <button
                  onClick={clearFilters}
                  className="ml-auto text-xs text-gray-400 hover:text-white flex items-center border border-border-dark bg-background-hover hover:bg-background-darker px-3 py-1.5 rounded-md transition-colors"
                >
                  <X size={14} className="mr-1" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4 flex justify-between items-center">
        <div className="text-sm text-gray-400">
          Showing <span className="text-white font-medium">{filteredProducts.length}</span> of <span className="text-white font-medium">{products.length}</span> products
          {(productTypeFilter !== 'all' || searchQuery) && (
            <span className="ml-1">
              with filters
              {productTypeFilter !== 'all' && (
                <span className="ml-1 bg-primary/20 text-primary-300 text-xs py-0.5 px-2 rounded-full">
                  {productTypeFilter}
                </span>
              )}
              {searchQuery && (
                <span className="ml-1 bg-blue-700/20 text-blue-300 text-xs py-0.5 px-2 rounded-full">
                  "{searchQuery}"
                </span>
              )}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            className={`p-2 rounded-md border ${
              viewMode === 'grid'
                ? 'bg-primary/20 border-primary/40 text-primary-300'
                : 'border-border-dark bg-background-hover text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => toggleViewMode('grid')}
            title="Grid View"
            aria-label="Switch to Grid View"
          >
            <LayoutGrid size={18} />
          </button>
          <button
            className={`p-2 rounded-md border ${
              viewMode === 'table'
                ? 'bg-primary/20 border-primary/40 text-primary-300'
                : 'border-border-dark bg-background-hover text-gray-400 hover:text-gray-200'
            }`}
            onClick={() => toggleViewMode('table')}
            title="Table View"
            aria-label="Switch to Table View"
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {showContentLoading ? (
        <ContentLoader
          message={isInitialLoading ? 'Loading products...' : 'Updating products...'}
          size="lg"
        />
      ) : filteredProducts.length === 0 ? (
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <AlertCircle size={48} className="text-text-muted-dark mb-4" />
            <h3 className="text-xl font-medium mb-2">No Products Found</h3>
            <p className="text-text-muted-dark text-center max-w-md mb-6">
              {productTypeFilter === 'all' && !searchQuery
                ? "You haven't added any products yet. Add a product to start configuring integrations for your tenants."
                : "No products match your current filters. Try adjusting your search criteria or clear filters."
              }
            </p>
            {productTypeFilter === 'all' && !searchQuery ? (
              canManageProducts() && (
                <button 
                  onClick={openOnboardingModal}
                  className="premium-button-primary"
                >
                  Add Your First Product
                </button>
              )
            ) : (
              <button 
                onClick={clearFilters}
                className="premium-button-outline"
              >
                <X size={16} className="mr-2" />
                Clear Filters
              </button>
            )}
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => {
            const productId = product._id || product.id;
            
            if (!productId) {
              console.warn(`Product ${product.name} has no ID property!`);
            }
            
            return (
              <div 
                key={productId || `product-${product.name}`} 
                className="card hover:border-primary transition-colors relative z-0 cursor-pointer p-4 hover:shadow-md hover:shadow-primary/5 flex flex-col min-h-[220px]"
                onClick={() => productId && openProductDetailModal(productId)}
              >
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    (product as any).type === 'logger' 
                      ? 'bg-green-700/20 text-green-300' 
                      : 'bg-blue-700/20 text-blue-300'
                  }`}>
                    {(product as any).type === 'logger' ? 'Logger' : 'SaaS'}
                  </span>
                </div>
                
                <div className="flex items-start mb-2">
                  {product.logo_url ? (
                    <img 
                      src={product.logo_url} 
                      alt={product.name}
                      className="h-10 w-10 mr-2 object-contain bg-white rounded p-1"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '';
                        (e.target as HTMLImageElement).classList.add('hidden');
                      }}
                    />
                  ) : (
                    <div className="h-10 w-10 mr-2 flex items-center justify-center bg-background-hover rounded">
                      <ImagePlus size={18} className="text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-medium text-sm">{product.name}</h3>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {product.tags?.productName && (
                        <span className="px-1.5 py-0.5 text-xs bg-secondary-700/20 text-secondary-300 rounded">
                          {product.tags.productName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-border-dark pt-2 mb-2 flex-grow">
                  {(product as any).type === 'logger' ? (
                    <div className="space-y-1">
                      {(product as any).logger_config && (
                        <>
                          <div className="flex items-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></div>
                            <span className="text-xs">Product Type: {product.tags?.productType || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></div>
                            <span className="text-xs">Log Type: {product.tags?.logType || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></div>
                            <span className="text-xs">Keywords: {(product as any).logger_config.keywords?.length || 0}</span>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-text-muted-dark mb-1.5">
                        {product.integration_fields?.length || 0} integration {(product.integration_fields?.length || 0) === 1 ? 'field' : 'fields'}
                      </p>
                      <div className="space-y-1">
                        {product.integration_fields?.slice(0, 3).map((field) => (
                          <div key={field.field_key} className="flex items-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-500 mr-1.5"></div>
                            <span className="text-xs">
                              {field.label}
                              {field.is_required && (
                                <span className="text-status-danger ml-1">*</span>
                              )}
                            </span>
                          </div>
                        ))}
                        {(product.integration_fields?.length || 0) > 3 && (
                          <div className="text-xs text-primary">
                            +{(product.integration_fields?.length || 0) - 3} more fields
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-end space-x-1 border-t border-border-dark pt-3 relative z-10 mt-auto">
                  {productId && (
                    <>
                      <button
                        className="btn btn-icon btn-sm p-1.5 hover:bg-background-hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          openProductDetailModal(productId);
                        }}
                        title="View Details"
                      >
                        <Eye size={12} />
                      </button>
                      {canManageProducts() && (
                        <button
                          className="btn btn-icon btn-sm p-1.5 hover:bg-background-hover"
                          onClick={(e) => {
                            e.stopPropagation();
                            openProductEditModal(productId);
                          }}
                          title="Edit Product"
                        >
                          <Edit size={12} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Table View for Products
        <div className="rounded-lg border border-border-dark overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-background-hover border-b border-border-dark">
                  <th className="px-4 py-3 text-left font-medium text-sm">Product</th>
                  <th className="px-4 py-3 text-left font-medium text-sm">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-sm">Fields</th>
                  <th className="px-4 py-3 text-right font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const productId = product._id || product.id;
                  
                  return (
                    <tr 
                      key={productId || `product-${product.name}`}
                      className="border-t border-border-dark hover:bg-background-hover/40 cursor-pointer transition-colors"
                      onClick={() => productId && openProductDetailModal(productId)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          {product.logo_url ? (
                            <img 
                              src={product.logo_url} 
                              alt={product.name}
                              className="h-8 w-8 mr-3 object-contain bg-white rounded p-1 flex-shrink-0"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '';
                                (e.target as HTMLImageElement).classList.add('hidden');
                              }}
                            />
                          ) : (
                            <div className="h-8 w-8 mr-3 flex items-center justify-center bg-background-hover rounded flex-shrink-0">
                              <ImagePlus size={16} className="text-gray-500" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-sm">{product.name}</div>
                            {product.tags?.productName && (
                              <span className="text-xs text-text-muted-dark">{product.tags.productName}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          (product as any).type === 'logger' 
                            ? 'bg-green-700/20 text-green-300' 
                            : 'bg-blue-700/20 text-blue-300'
                        }`}>
                          {(product as any).type === 'logger' ? 'Logger' : 'SaaS'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {(product as any).type === 'logger' ? (
                          <span className="text-xs">
                            {(product as any).logger_config?.keywords?.length || 0} keywords
                          </span>
                        ) : (
                          <span className="text-xs">
                            {product.integration_fields?.length || 0} integration {(product.integration_fields?.length || 0) === 1 ? 'field' : 'fields'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end space-x-2">
                          {productId && (
                            <>
                              <button
                                className="premium-button-outline py-1 px-3 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openProductDetailModal(productId);
                                }}
                                title="View Details"
                              >
                                <span className="flex items-center">
                                  <Eye size={12} className="mr-1" />
                                  View
                                </span>
                              </button>
                              {canManageProducts() && (
                                <button
                                  className="premium-button-outline py-1 px-3 text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openProductEditModal(productId);
                                  }}
                                >
                                  <span className="flex items-center">
                                    <Edit size={12} className="mr-1" />
                                    Edit
                                  </span>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Product detail modal */}
      {isDetailModalOpen && selectedProductId && (
        <ProductDetailModal 
          productId={selectedProductId} 
          onClose={closeProductDetailModal}
          onEdit={openProductEditModal}
        />
      )}
      
      {/* Product edit modal */}
      {isEditModalOpen && selectedProductForEdit && (
        <ProductEditModal 
          productId={selectedProductForEdit} 
          onClose={closeProductEditModal}
          onSaved={handleProductSaved}
        />
      )}
      
      {/* Product onboarding modal */}
      {isOnboardingModalOpen && (
        <ProductOnboardingModal 
          onClose={closeOnboardingModal}
          onProductAdded={handleProductSaved}
        />
      )}
    </div>
  );
};

export default ProductsListPage; 